import React, { useState } from 'react';
import { FaSync, FaInfoCircle, FaCheck, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import '../styles/cropRotation.css';

const CropRotationEngine = () => {
  const [recommendations, setRecommendations] = useState([
    {
      id: 1,
      region: 'Village A',
      currentCrop: 'Wheat',
      recommendedCrop: 'Millet',
      reason: 'Soil nutrient depletion, pest cycle disruption',
      status: 'pending',
      impactScore: 85
    },
    {
      id: 2,
      region: 'Village B',
      currentCrop: 'Corn',
      recommendedCrop: 'Legumes',
      reason: 'Nitrogen fixation, soil structure improvement',
      status: 'pending',
      impactScore: 78
    },
    {
      id: 3,
      region: 'Hillside Area',
      currentCrop: 'Soybeans',
      recommendedCrop: 'Reserve 20% for pollinator habitat',
      reason: 'Biodiversity enhancement, natural pest control',
      status: 'approved',
      impactScore: 92
    },
    {
      id: 4,
      region: 'Village C',
      currentCrop: 'Rice',
      recommendedCrop: 'Delay planting by 2 weeks',
      reason: 'Break stem borer cycle affecting Village D',
      status: 'pending',
      impactScore: 70
    }
  ]);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleApprove = (id) => {
    setRecommendations(recommendations.map(rec =>
      rec.id === id ? { ...rec, status: 'approved' } : rec
    ));
    const rec = recommendations.find(r => r.id === id);
    showNotification(`Approved recommendation for ${rec.region}`, 'success');
  };

  const handleReject = (id) => {
    setRecommendations(recommendations.map(rec =>
      rec.id === id ? { ...rec, status: 'rejected' } : rec
    ));
    const rec = recommendations.find(r => r.id === id);
    showNotification(`Rejected recommendation for ${rec.region}`, 'info');
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate API call
    setTimeout(() => {
      setIsRefreshing(false);
      showNotification('Recommendations refreshed successfully', 'success');
    }, 1500);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <FaCheck />;
      case 'rejected': return <FaTimes />;
      default: return <FaExclamationTriangle />;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return 'Pending';
    }
  };

  return (
    <div className="crop-rotation-engine">
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 20px',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#2196f3',
            color: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 9999,
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      <div className="section-header">
        <h2>AI-Driven Regional Crop Rotation Engine</h2>
        <button
          className="refresh-button"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <FaSync className={isRefreshing ? 'rotating' : ''} />
          {isRefreshing ? 'Refreshing...' : 'Refresh Recommendations'}
        </button>
      </div>

      <div className="recommendations-list">
        {recommendations.map(rec => (
          <div key={rec.id} className="recommendation-card">
            <div className={`status-indicator status-${rec.status}`}></div>
            
            <div className="recommendation-header">
              <h3>{rec.region}</h3>
              <div className="recommendation-status" style={{ color: getStatusColor(rec.status) }}>
                {getStatusIcon(rec.status)} {getStatusText(rec.status)}
              </div>
            </div>
            
            <div className="recommendation-content">
              <div className="recommendation-row">
                <span className="label">Current Crop:</span>
                <span className="value">{rec.currentCrop}</span>
              </div>
              <div className="recommendation-row">
                <span className="label">Recommended Action:</span>
                <span className="value highlight">{rec.recommendedCrop}</span>
              </div>
              <div className="recommendation-row">
                <span className="label">Impact Score:</span>
                <span className="value highlight">{rec.impactScore}/100</span>
              </div>
              <div className="recommendation-row">
                <span className="label">Reasoning:</span>
                <span className="value reason">
                  <FaInfoCircle /> {rec.reason}
                </span>
              </div>
            </div>
            
            {rec.status === 'pending' && (
              <div className="recommendation-actions">
                <button 
                  className="approve-button"
                  onClick={() => handleApprove(rec.id)}
                >
                  <FaCheck /> Approve
                </button>
                <button 
                  className="reject-button"
                  onClick={() => handleReject(rec.id)}
                >
                  <FaTimes /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch(status) {
    case 'approved': return 'var(--accent-green)';
    case 'rejected': return 'var(--accent-red)';
    default: return 'var(--accent-yellow)';
  }
};

export default CropRotationEngine; 